import Joi from 'joi';
import { TFunction } from 'react-i18next';
import { DEFAULT_MAX_LENGTH, OPTIONS_TEXT_MAX_LENGTH, VIDEO_RESPONSE_MAX_LENGTH } from '../../questionnaireTemplate.constants';
import { LANGUAGE_CODE, QUESTIONNAIRE_ELEMENT_TYPE } from '../../questionnaireTemplate.enums';

// ─── Schema Error Key Maps ────────────────────────────────────────────────────
// Schemas are built once with i18n keys as message values.
// Translation happens at display time, not at schema-build time.
// This means schemas are language-agnostic and can be created outside React.

const OPTION_TEXT_MESSAGES = {
  'string.base': 'formBuilder.validationErrors.optionRequired',
  'string.empty': 'formBuilder.validationErrors.optionEmpty',
  'any.required': 'formBuilder.validationErrors.optionRequired',
  'string.min': 'formBuilder.validationErrors.optionMin',
  'string.max': 'formBuilder.validationErrors.optionMax',
} as const;

const QUESTION_TEXT_MESSAGES = {
  'string.base': 'formBuilder.validationErrors.questionTextRequired',
  'string.empty': 'formBuilder.validationErrors.questionTextEmpty',
  'any.required': 'formBuilder.validationErrors.questionTextRequired',
  'string.min': 'formBuilder.validationErrors.questionTextMin',
  'string.max': 'formBuilder.validationErrors.questionTextMax',
} as const;

const MAX_LENGTH_MESSAGES = {
  'number.base': 'formBuilder.validationErrors.maxLengthBase',
  'number.integer': 'formBuilder.validationErrors.maxLengthInteger',
  'number.min': 'formBuilder.validationErrors.maxLengthMin',
  'number.max': 'formBuilder.validationErrors.maxLengthMax',
  'any.required': 'formBuilder.validationErrors.maxLengthRequired',
} as const;

const TEMPLATE_NAME_MESSAGES = {
  'string.base': 'formBuilder.validationErrors.templateNameRequired',
  'string.empty': 'formBuilder.validationErrors.templateNameEmpty',
  'any.required': 'formBuilder.validationErrors.templateNameRequired',
  'string.min': 'formBuilder.validationErrors.templateNameMin',
  'string.max': 'formBuilder.validationErrors.templateNameMax',
} as const;

const LOCALE_MESSAGES = {
  'any.only': 'formBuilder.validationErrors.localeOnly',
  'any.required': 'formBuilder.validationErrors.localeRequired',
  'string.empty': 'formBuilder.validationErrors.localeEmpty',
} as const;

const DEFAULT_MESSAGES = {
  'boolean.base': 'formBuilder.validationErrors.defaultBase',
  'any.required': 'formBuilder.validationErrors.defaultRequired',
} as const;

const QUESTIONS_MESSAGES = {
  'array.min': 'formBuilder.validationErrors.questionsMin',
  'any.required': 'formBuilder.validationErrors.questionsRequired',
} as const;

const IMAGE_MESSAGES = {
  'array.min': 'formBuilder.validationErrors.imageMin',
  'any.required': 'formBuilder.validationErrors.imageRequired',
} as const;

export const optionTextSchema = Joi.string().min(1).max(OPTIONS_TEXT_MAX_LENGTH).required().messages(OPTION_TEXT_MESSAGES);

export const optionSchema = Joi.object({
  id: Joi.string().required(),
  text: optionTextSchema,
  priority: Joi.number().optional(),
  isPreferredAnswer: Joi.boolean().optional(),
});

export const imageSchema = Joi.array()
  .items(
    Joi.object({
      galleryId: Joi.string().required(),
      url: Joi.string().required(),
      path: Joi.string().optional(),
    }),
  )
  .when('questionType', {
    is: Joi.valid(QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE_WITH_IMAGE, QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE),
    then: Joi.array().min(1).required().messages(IMAGE_MESSAGES),
    otherwise: Joi.array().optional(),
  });

export const questionElementSchema = Joi.object({
  id: Joi.string().required(),
  question: Joi.string()
    .min(1)
    .when('questionType', {
      is: QUESTIONNAIRE_ELEMENT_TYPE.VIDEO_RESPONSE,
      then: Joi.string().max(VIDEO_RESPONSE_MAX_LENGTH),
      otherwise: Joi.string().max(DEFAULT_MAX_LENGTH),
    })
    .required()
    .messages(QUESTION_TEXT_MESSAGES),
  questionType: Joi.string()
    .valid(...Object.values(QUESTIONNAIRE_ELEMENT_TYPE))
    .required(),
  questionOrder: Joi.number().required(),
  maxLength: Joi.when('questionType', {
    is: QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM,
    then: Joi.number().integer().min(1).max(1000).required().messages(MAX_LENGTH_MESSAGES),
    otherwise: Joi.number().integer().min(1).max(1000).optional().messages(MAX_LENGTH_MESSAGES),
  }),
  options: Joi.array().items(optionSchema).optional(),
  followups: Joi.array().items(Joi.link('/')).optional(),
  knockout: Joi.boolean().optional(),
  isPausable: Joi.boolean().optional(),
  shouldReaskOnReapply: Joi.boolean().optional(),
  preferredAnswer: Joi.boolean().optional(),
  preferredAnswers: Joi.array().items(Joi.string()).optional(),
  parent: Joi.string().allow(null).optional(),
  optionId: Joi.string().allow(null).optional(),
  images: imageSchema,
}).unknown(true);

export const templateNameSchema = Joi.string().min(1).max(50).required().messages(TEMPLATE_NAME_MESSAGES);

export const payloadSchema = Joi.object({
  name: templateNameSchema,
  questionLocale: Joi.string()
    .valid(...Object.values(LANGUAGE_CODE))
    .required()
    .messages(LOCALE_MESSAGES),
  default: Joi.boolean().required().default(false).messages(DEFAULT_MESSAGES),
  questions: Joi.array().min(1).items(questionElementSchema).required().messages(QUESTIONS_MESSAGES),
}).unknown(true);

// ─── Translation Helper ───────────────────────────────────────────────────────
// Call this after validation to translate the raw i18n key in error.message.

export const translateValidationError = (
  error: Joi.ValidationError | undefined,
  t: TFunction<'questionnaireTemplate'>,
): string | null => {
  if (!error) return null;
  // error.message is now an i18n key — translate it
  return t(error.message as any) ?? error.message;
};

// Translate all errors from a ValidationError (for multi-field forms)
export const translateValidationErrors = (
  error: Joi.ValidationError | undefined,
  t: TFunction<'questionnaireTemplate'>,
): Record<string, string> => {
  if (!error) return {};
  return Object.fromEntries(error.details.map((detail) => [detail.path.join('.'), t(detail.message as any) ?? detail.message]));
};
